import * as React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import {
  ListItem,
  ListItemContent,
  ListItemTitle,
  ListItemDescription,
  ListItemVisual,
  ListItemExtras,
  ListItemCheckbox,
} from 'iam/components/ListItem'
import { useTask } from 'hooks/mobx-hooks'
import { useScrollOffsetSpring } from 'hooks/spring-hooks'
import { extractMessageFromError } from 'utils/error-util'
import { clamp } from 'utils/math-util'
import Icon from 'components/Icon'
import Spinner from 'components/Spinner'
import IconVisual from 'components/IconVisual'
import Button from 'components/Button'
import ScrollShadow from 'components/ScrollShadow'
import SelectionViewModel from '../SelectionViewModel'
import SectionEmpty from '../SectionEmpty'
import styles from './SelectionList.sass'
import InfiniteScrollLoader from 'components/InfiniteScrollLoader'

interface CommonProps<T> {
  editing?: boolean
  selectionVM: SelectionViewModel<T>
  icon: React.ReactNode

  getContent(item: T): {
    title: string
    description: string
  }
}

interface SelectionListProps<T> extends CommonProps<T> {}

/**
 * This is used as a viewer and editor for skills and roles.
 */
export default observer(function SelectionList<T>({
  selectionVM,
  getContent,
  editing,
  icon,
}: SelectionListProps<T>) {
  const items = editing ? selectionVM.filteredItems : selectionVM.selectedItems
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const searchFieldRef = React.useRef<HTMLInputElement>(null)
  const scrollOffset = useScrollOffsetSpring(scrollRef)
  const { scrollProvider } = selectionVM
  const onSearchBarClick = React.useCallback(
    () => searchFieldRef.current && searchFieldRef.current.focus(),
    [],
  )
  const renderItems = () =>
    items.length === 0 ? (
      <SectionEmpty>Nothing matches your search.</SectionEmpty>
    ) : (
      items.map((item, i) => (
        <SelectionListItem
          isFirstItem={i === 1}
          key={selectionVM.getItemId(item)}
          item={item}
          getContent={getContent}
          icon={icon}
          selectionVM={selectionVM}
          editing={editing}
        />
      ))
    )

  return (
    <div className={cx(styles.root, editing && styles.editing)}>
      {editing && (
        <div className={styles.searchField} onClick={onSearchBarClick}>
          <span className={styles.searchIcon}>
            <Icon name="search" />
          </span>
          <input
            ref={searchFieldRef}
            type="search"
            placeholder="Search"
            className={styles.searchInput}
            onChange={(e) => selectionVM.setSearchText(e.target.value)}
          />
          <ScrollShadow opacity={scrollOffset.to((v) => clamp(v / 50))} />
        </div>
      )}
      <div className={styles.items} ref={scrollRef}>
        {editing
          ? // In Edit-mode, we render all items, which means we fetch first.
            // If not in Edit-model, we can render immediately
            scrollProvider.fetch.match({
              pending: () => <Loading />,
              rejected: (err) => (
                <Error error={err} onTryAgainClick={scrollProvider.fetch} />
              ),
              resolved: () => (
                <>
                  {renderItems()}
                  {!scrollProvider.isDone && (
                    <InfiniteScrollLoader scrollProvider={scrollProvider} />
                  )}
                </>
              ),
            })
          : renderItems()}
      </div>
    </div>
  )
})

interface SelectionListItemProps<T> extends CommonProps<T> {
  item: T
  isFirstItem: boolean
}

const SelectionListItem = observer(function SelectionListItem<T>({
  item,
  getContent,
  icon,
  editing,
  isFirstItem,
  selectionVM,
}: SelectionListItemProps<T>) {
  const content = getContent(item)
  const toggle = useTask.resolved(
    () => selectionVM.toggle(item),
    [selectionVM, item],
  )
  const canUpdate = selectionVM.canUpdate(item)
  const disabled = toggle.pending || canUpdate === false
  return (
    // Edit-mode renders a search bar.
    <ListItem
      hasListHeader={editing && isFirstItem}
      onClick={editing && canUpdate ? toggle : undefined}
      disabled={disabled}
    >
      <ListItemVisual>
        <IconVisual tight>{icon}</IconVisual>
      </ListItemVisual>
      <ListItemContent>
        <ListItemTitle>{content.title}</ListItemTitle>
        {content.description && (
          <ListItemDescription title={content.description}>
            {content.description}
          </ListItemDescription>
        )}
      </ListItemContent>
      {editing && (
        <ListItemExtras>
          <ListItemCheckbox
            disabled={disabled}
            loading={toggle.pending}
            checked={selectionVM.isSelected(item)}
          />
        </ListItemExtras>
      )}
    </ListItem>
  )
})

const Loading = () => (
  <div className={styles.loading}>
    <Spinner />
  </div>
)

interface ErrorProps {
  error: unknown

  onTryAgainClick(): unknown
}

const Error = ({ error, onTryAgainClick }: ErrorProps) => (
  <div className={styles.error}>
    <Icon name="info" rotate={180} />
    <div className={styles.errorText}>{extractMessageFromError(error)}</div>
    <div className={styles.errorActions}>
      <Button type="translucent" onClick={onTryAgainClick}>
        Try Again
      </Button>
    </div>
  </div>
)
